/**
 * Last good response per `useApiData` key, kept in memory for the session.
 *
 * React Query keeps its cache across navigations; the older `useApiData`
 * hooks (reviews, social, reports, companies, workflows…) started every
 * mount from nothing. Offline, coming back to one of those pages threw away
 * what had loaded a minute ago and showed "Couldn't load" under a banner that
 * said "Showing what was loaded" (UX-018). With this, the page re-opens on
 * its last good data while it refreshes, and keeps it if the refresh fails.
 *
 * Memory only (never storage): cleared on sign-out, capped in size.
 */
interface Entry {
  data: unknown
  at: number
}

const MAX_ENTRIES = 120
const cache = new Map<string, Entry>()

export function readApiData<T>(key: string): { data: T; at: number } | null {
  const hit = cache.get(key)
  return hit ? { data: hit.data as T, at: hit.at } : null
}

export function writeApiData(key: string, data: unknown): void {
  cache.delete(key)
  cache.set(key, { data, at: Date.now() })
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
}

/** Sign-out (and tests): the next person on this browser starts clean. */
export function clearApiDataCache(): void {
  cache.clear()
}
